import { useState } from 'react'

import { ErrorList } from '#app/components/form/error-list.tsx'
import { ADVERSE_EVENTS_SECTION_RESOURCES } from '#app/routes/mocra+/_form+/adverse-events/section-resources.ts'
import { AeSystemAdd } from '#app/routes/mocra+/_form+/components/ae-systems/add.tsx'
import { AeSystemCard } from '#app/routes/mocra+/_form+/components/ae-systems/card.tsx'
import { AeSystemEditOverlay } from '#app/routes/mocra+/_form+/components/ae-systems/edit.tsx'
import {
	createAeSystemDraft,
	sanitizeAeSystemDraft,
	type AeSystemDraft,
} from '#app/routes/mocra+/_form+/components/ae-systems/types.ts'
import { Section } from '#app/routes/mocra+/_form+/components/section.tsx'

type AeSystemListItem = {
	readonly draft: AeSystemDraft
	readonly errors: readonly string[]
}

type Props = {
	readonly systems: ReadonlyArray<AeSystemListItem>
	readonly warnings: ReadonlyArray<string>
	readonly onAdd: (draft: AeSystemDraft) => void
	readonly onEdit: (index: number, draft: AeSystemDraft) => void
	readonly onRemove: (index: number) => void
	readonly disableRemove?: boolean
}

export function ComplaintSystemsSection({
	systems,
	warnings,
	onAdd,
	onEdit,
	onRemove,
	disableRemove = false,
}: Props) {
	const [editingIndex, setEditingIndex] = useState<number | null>(null)

	const editingDraft =
		editingIndex !== null && systems[editingIndex]
			? systems[editingIndex].draft
			: createAeSystemDraft()

	return (
		<Section
			title="Complaint systems of record"
			description="Document the systems where complaints land so you can link each intake channel to its downstream workflow."
			mocraSections={ADVERSE_EVENTS_SECTION_RESOURCES.mocraSections}
			templates={ADVERSE_EVENTS_SECTION_RESOURCES.templates}
			meta={
				warnings.length > 0 ? <ErrorList errors={[...warnings]} /> : undefined
			}
		>
			<div className="space-y-3">
				{systems.length > 0 ? (
					systems.map((system, index) => {
						const sanitized = sanitizeAeSystemDraft(system.draft)
						const errors = system.errors
						return (
							<AeSystemCard
								key={`complaint-system-${index}`}
								system={sanitized}
								onEdit={() => setEditingIndex(index)}
								onRemove={() => onRemove(index)}
								removeDisabled={disableRemove}
								errors={errors.length > 0 ? [...errors] : undefined}
							/>
						)
					})
				) : (
					<p className="text-muted-foreground text-sm">
						No complaint systems documented yet.
					</p>
				)}
			</div>
			<AeSystemAdd
				onSubmit={(draft) => {
					onAdd(draft)
				}}
			/>
			<AeSystemEditOverlay
				open={editingIndex !== null}
				onOpenChange={(open) => {
					if (!open) {
						setEditingIndex(null)
					}
				}}
				onSubmit={(draft) => {
					if (editingIndex === null) return
					onEdit(editingIndex, draft)
					setEditingIndex(null)
				}}
				initialValue={editingDraft}
				onClosed={() => setEditingIndex(null)}
				onCancel={() => setEditingIndex(null)}
			/>
		</Section>
	)
}
